import {
  Avatar,
  Box,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Badge } from '@trm/ui-kit-admin';
import { useEffect } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { handlerActiveItem, handlerDrawerOpen, useGetMenuMaster } from '~/api/menu';
import IconButton from '~/components/@extended/IconButton';
// types
import { ThemeMode } from '~/types/config';
import { LinkTarget, NavActionType, NavItemType } from '~/types/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
}

const NavItem = ({ item, level, isParents = false }: Props) => {
  const theme = useTheme();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const openItem = menuMaster.openedItem;

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const Icon = item.icon!;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
      }}
    />
  ) : (
    false
  );

  const url = item.url!;
  const badge =
    item.beta?.kind === 'preview' ? (
      <Badge color="brand" size="sm">
        Beta
      </Badge>
    ) : (
      item.badge
    );

  const { pathname } = useLocation();
  const isSelected =
    [url, ...(item.activeUrls ?? [])].some((path) => !!matchPath({ path, end: false }, pathname)) ||
    openItem === item.id;

  // active menu item on page load
  useEffect(() => {
    if (pathname === url) handlerActiveItem(item.id!);
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = theme.palette.mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
  const iconSelectedColor =
    theme.palette.mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'primary.main';

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        title={item.title}
        placement="right"
        arrow
        disableHoverListener={drawerOpen}
        slotProps={{
          popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] },
        }}
      >
        <ListItemButton
          component={Link}
          data-cy={item.id}
          to={url}
          target={itemTarget}
          {...(itemTarget === '_blank' && { rel: 'noopener noreferrer' })}
          disabled={item.disabled}
          selected={isSelected}
          sx={{
            zIndex: 1201,
            pl: drawerOpen ? `${level * 28}px` : 1.5,
            py: !drawerOpen && level === 1 ? 1.25 : 1,
            ...(drawerOpen && {
              '&:hover': {
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
              },
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
                borderRight: `2px solid ${theme.palette.primary.main}`,
                color: iconSelectedColor,
                '&:hover': {
                  color: iconSelectedColor,
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
                },
              },
            }),
            ...(!drawerOpen && {
              '&:hover': {
                bgcolor: 'transparent',
              },
              '&.Mui-selected': {
                '&:hover': {
                  bgcolor: 'transparent',
                },
                bgcolor: 'transparent',
              },
            }),
          }}
          {...(downLG && {
            onClick: () => handlerDrawerOpen(false),
          })}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                color: isSelected ? iconSelectedColor : textColor,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor:
                      theme.palette.mode === ThemeMode.DARK
                        ? 'secondary.light'
                        : 'secondary.lighter',
                  },
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor:
                      theme.palette.mode === ThemeMode.DARK ? 'primary.900' : 'primary.lighter',
                    '&:hover': {
                      bgcolor:
                        theme.palette.mode === ThemeMode.DARK
                          ? 'primary.darker'
                          : 'primary.lighter',
                    },
                  }),
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: isSelected ? iconSelectedColor : textColor,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {item.title}
                  </Typography>
                  {badge && <Box sx={{ flexShrink: 0 }}>{badge}</Box>}
                </Box>
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && !badge && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      </Tooltip>
      {(drawerOpen || (!drawerOpen && level !== 1)) &&
        item?.actions &&
        item?.actions.map((action, index) => {
          const ActionIcon = action.icon!;
          const callAction = action?.function;
          return (
            <IconButton
              key={index}
              {...(action.type === NavActionType.FUNCTION && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                },
              })}
              {...(action.type === NavActionType.LINK && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self',
                ...(action.target && { rel: 'noopener noreferrer' }),
              })}
              color="secondary"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 12,
                right: 20,
                zIndex: 1202,
                width: 20,
                height: 20,
                mr: -1,
                ml: 1,
                color: 'secondary.dark',
                borderColor: isSelected ? 'primary.light' : 'secondary.light',
                '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' },
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
};

export default NavItem;
